import { useEffect, useRef, useState } from 'react'
import {
  FileSignature,
  Plus,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Download,
  ExternalLink,
  Upload,
  Loader2,
  Mail,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { AdminCustomer, DocumentSignature, DocumentSignatureStatus } from '../../types'

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  DocumentSignatureStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  pending: {
    label: 'En attente',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    icon: Clock,
  },
  signed: {
    label: 'Signé',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    icon: CheckCircle,
  },
  refused: {
    label: 'Refusé',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    icon: XCircle,
  },
  expired: {
    label: 'Expiré',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300',
    icon: AlertCircle,
  },
}

function StatusBadge({ status }: { status: DocumentSignatureStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending
  const Icon = cfg.icon
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

// ── Contact type ──────────────────────────────────────────────────────────────

interface ERPContact {
  id: string
  email: string
  firstname: string
  lastname: string
  fullname: string
  phone: string
  poste: string
}

// ── Upload modal ──────────────────────────────────────────────────────────────

interface UploadModalProps {
  customer: AdminCustomer
  onClose: () => void
  onCreated: (ds: DocumentSignature) => void
}

function UploadModal({ customer, onClose, onCreated }: UploadModalProps) {
  const [documentName, setDocumentName] = useState('')
  const [locale, setLocale] = useState('fr')
  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [contacts, setContacts] = useState<ERPContact[]>([])
  const [contactsLoading, setContactsLoading] = useState(true)
  const [contactsError, setContactsError] = useState('')
  const [selectedContacts, setSelectedContacts] = useState<ERPContact[]>([])

  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api
      .getAdminDocumentSignatureContacts(customer.id)
      .then((data) => {
        setContacts(data)
        if (data.length === 1) setSelectedContacts(data)
        if (data.length === 0) setContactsError("Aucun contact trouvé pour ce client dans l'ERP.")
      })
      .catch(() => setContactsError("Impossible de charger les contacts depuis l'ERP."))
      .finally(() => setContactsLoading(false))
  }, [customer.id])

  const handleFile = (file: File) => {
    setFileError('')
    if (file.type !== 'application/pdf') {
      setFileError('Seuls les fichiers PDF sont acceptés.')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setFileError('Le fichier ne doit pas dépasser 15 Mo.')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      const b64 = result.split(',')[1] ?? ''
      setPdfBase64(b64)
      setFileName(file.name)
      if (!documentName) setDocumentName(file.name.replace(/\.pdf$/i, ''))
    }
    reader.readAsDataURL(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const toggleContact = (c: ERPContact) =>
    setSelectedContacts((prev) =>
      prev.some((s) => s.email === c.email) ? prev.filter((s) => s.email !== c.email) : [...prev, c]
    )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pdfBase64) {
      setError('Veuillez sélectionner un fichier PDF.')
      return
    }
    if (selectedContacts.length === 0) {
      setError('Veuillez sélectionner au moins un signataire.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const result = await api.createAdminDocumentSignature({
        document_name: documentName,
        pdf_base64: pdfBase64,
        signers: selectedContacts.map((c) => ({
          email: c.email,
          first_name: c.firstname,
          last_name: c.lastname,
        })),
        locale,
        customer_id: customer.id,
      })
      const full = await api.getDocumentSignature(result.id)
      onCreated(full)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erreur lors de l'envoi."
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Nouveau document à signer
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Client : <span className="font-medium">{customer.customer_name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* PDF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fichier PDF <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
            >
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleFile(f)
                }}
              />
              {fileName ? (
                <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{fileName}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Upload className="w-8 h-8" />
                  <p className="text-sm">Glissez un PDF ici ou cliquez pour parcourir</p>
                  <p className="text-xs">PDF uniquement · max 15 Mo</p>
                </div>
              )}
            </div>
            {fileError && <p className="text-xs text-red-500 mt-1">{fileError}</p>}
          </div>

          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom du document <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Ex : Contrat de prestation"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Contacts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Signataires <span className="text-red-500">*</span>
              </label>
              {contacts.length > 1 && (
                <span className="text-xs text-gray-400">
                  {selectedContacts.length} sélectionné{selectedContacts.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {contactsLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              </div>
            )}

            {!contactsLoading && contactsError && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-400">{contactsError}</p>
              </div>
            )}

            {!contactsLoading && contacts.length > 0 && (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {contacts.map((c) => {
                  const isSelected = selectedContacts.some((s) => s.email === c.email)
                  return (
                    <button
                      key={c.id || c.email}
                      type="button"
                      onClick={() => toggleContact(c)}
                      className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-gray-300 dark:border-gray-500'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                            <path
                              d="M1 4l3 3 5-6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {c.fullname || c.email}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          {c.email}
                        </p>
                        {c.poste && <p className="text-xs text-gray-400 truncate">{c.poste}</p>}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Langue */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Langue de l&apos;email
            </label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!pdfBase64 || selectedContacts.length === 0 || submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi…
                </>
              ) : (
                <>
                  <FileSignature className="w-4 h-4" />
                  Envoyer pour signature
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminDocumentSignatures() {
  useTranslation()
  const searchRef = useRef<HTMLDivElement>(null)

  // Customer selector
  const [customerSearch, setCustomerSearch] = useState('')
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<AdminCustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomer | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [customersLoaded, setCustomersLoaded] = useState(false)

  // Documents
  const [records, setRecords] = useState<DocumentSignature[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [offset, setOffset] = useState(0)
  const limit = 20

  // Load customers once
  useEffect(() => {
    api
      .getAdminCustomers()
      .then((res) => {
        setCustomers(res)
        setCustomersLoaded(true)
      })
      .catch(() => setCustomersLoaded(true))
  }, [])

  // Filter customers
  useEffect(() => {
    if (!customerSearch.trim()) {
      setFilteredCustomers(customers.slice(0, 20))
    } else {
      const q = customerSearch.toLowerCase()
      setFilteredCustomers(
        customers
          .filter(
            (c) =>
              c.customer_name?.toLowerCase().includes(q) ||
              c.email?.toLowerCase().includes(q) ||
              c.erp_customer_id?.toLowerCase().includes(q)
          )
          .slice(0, 20)
      )
    }
  }, [customerSearch, customers])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Load documents when customer changes
  useEffect(() => {
    if (!selectedCustomer) return
    setLoading(true)
    setListError(null)
    api
      .getAdminDocumentSignatures({ customer_id: selectedCustomer.id, limit, offset })
      .then((res) => {
        setRecords(res.data)
        setTotal(res.total)
      })
      .catch(() => setListError('Impossible de charger les documents.'))
      .finally(() => setLoading(false))
  }, [selectedCustomer, limit, offset])

  const selectCustomer = (c: AdminCustomer) => {
    setSelectedCustomer(c)
    setCustomerSearch(c.customer_name)
    setShowDropdown(false)
    setOffset(0)
    setRecords([])
  }

  const clearCustomer = () => {
    setSelectedCustomer(null)
    setCustomerSearch('')
    setRecords([])
    setTotal(0)
  }

  const handleCreated = (ds: DocumentSignature) => {
    setShowUpload(false)
    setRecords((prev) => [ds, ...prev])
    setTotal((t) => t + 1)
  }

  const handleDownload = async (rec: DocumentSignature) => {
    setDownloadingId(rec.id)
    try {
      const res = await api.downloadDocumentSignature(rec.id)
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${res.pdf}`
      link.download = `${res.document_name ?? rec.document_name}-signé.pdf`
      link.click()
    } catch {
      alert("Le document signé n'est pas encore disponible.")
    } finally {
      setDownloadingId(null)
    }
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-blue-600" />
          Signatures de documents
        </h1>
        {selectedCustomer && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouveau document
          </button>
        )}
      </div>

      {/* Customer selector */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 p-5">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Sélectionner un client
        </p>
        <div className="relative" ref={searchRef}>
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden bg-white dark:bg-gray-700">
            <Search className="w-4 h-4 text-gray-400 ml-3 flex-shrink-0" />
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value)
                if (selectedCustomer) setSelectedCustomer(null)
                setShowDropdown(true)
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={customersLoaded ? 'Rechercher un client…' : 'Chargement…'}
              className="flex-1 px-3 py-2.5 text-sm bg-transparent text-gray-900 dark:text-white outline-none"
            />
            {selectedCustomer && (
              <button onClick={clearCustomer} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {showDropdown && filteredCustomers.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-56 overflow-y-auto">
              {filteredCustomers.map((c) => (
                <button
                  key={c.id}
                  onMouseDown={() => selectCustomer(c)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {c.customer_name}
                  </span>
                  <span className="text-xs text-gray-500">{c.email || c.erp_customer_id}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedCustomer && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Client sélectionné :{' '}
            <strong className="text-gray-700 dark:text-gray-200">
              {selectedCustomer.customer_name}
            </strong>
            {selectedCustomer.email && <> — {selectedCustomer.email}</>}
          </p>
        )}
      </div>

      {/* Documents table */}
      {selectedCustomer && (
        <>
          {listError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
              {listError}
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
                <FileSignature className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Aucun document envoyé en signature pour ce client.</p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="mt-4 text-blue-600 dark:text-blue-400 text-sm hover:underline"
                >
                  Envoyer le premier document →
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Document</th>
                      <th className="px-4 py-3 text-left">Signataires</th>
                      <th className="px-4 py-3 text-left">Statut</th>
                      <th className="px-4 py-3 text-left">Envoyé le</th>
                      <th className="px-4 py-3 text-left">Signé le</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {records.map((rec) => (
                      <tr
                        key={rec.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {rec.document_name}
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            {rec.signers.map((s, i) => (
                              <div key={i} className="text-xs text-gray-600 dark:text-gray-300">
                                {s.first_name || s.last_name
                                  ? `${s.first_name} ${s.last_name} — ${s.email}`
                                  : s.email}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={rec.status} />
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {formatDate(rec.created_at)}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {formatDate(rec.signed_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {rec.status === 'pending' && rec.signing_url && (
                              <a
                                href={rec.signing_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Lien de signature
                              </a>
                            )}
                            {rec.status === 'signed' && (
                              <button
                                onClick={() => handleDownload(rec)}
                                disabled={downloadingId === rec.id}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloadingId === rec.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Download className="w-3.5 h-3.5" />
                                )}
                                Télécharger
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > limit && (
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>
                {offset + 1}–{Math.min(offset + limit, total)} sur {total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showUpload && selectedCustomer && (
        <UploadModal
          customer={selectedCustomer}
          onClose={() => setShowUpload(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}
